/**
 * Pricing matrix — accounts × billing channels. Each cell is an account-level
 * override (pricing_configurations with a null application); an empty cell uses
 * the platform default (default_pricings). A row may instead be assigned a
 * preset tariff (pricing_plans) — then its prices come from the tariff and the
 * cells are locked. Backed by the admin matrix + plans endpoints (admin 1.0.45).
 */

import { httpClient } from "@/utils/httpClient";
import type { NotifyEnvelope } from "@/types/api";

export interface PricingMatrixRow {
  accountId: string;
  accountName: string;
  costCenterId: string;
  costCenterName: string;
  /** Assigned tariff for the current scope (null = default/editable, or inherits when app-scoped). */
  pricingPlanId: string | null;
  /** Account-level tariff inherited when app-scoped and no per-app tariff (null otherwise). */
  inheritedPlanId: string | null;
  /** channel -> override for the current scope; absent channel = using the default. */
  overrides: Record<string, number>;
}

export interface PricingPlan {
  id: string;
  name: string;
  /** channel (lowercase) -> price; missing channel = platform default. */
  prices: Record<string, number>;
}

export interface ApplicationRef {
  id: string;
  name: string;
}

export interface PricingMatrix {
  channels: string[];
  defaults: Record<string, number>;
  plans: PricingPlan[];
  applications: ApplicationRef[];
  selectedApplicationId: string | null;
  rows: PricingMatrixRow[];
}

// The API serializes dictionary keys in camelCase (Telegram -> telegram,
// SMS -> sMS), which won't match the PascalCase channel names. Normalize all
// channel-keyed maps to lowercase so lookups are case-insensitive.
const lower = (o: Record<string, number>): Record<string, number> =>
  Object.fromEntries(
    Object.entries(o ?? {}).map(([k, v]) => [k.toLowerCase(), v]),
  );

const normalizePlan = (p: {
  id: string;
  name: string;
  prices: Record<string, number>;
}): PricingPlan => ({ id: p.id, name: p.name, prices: lower(p.prices) });

export const getPricingMatrix = async (
  applicationId?: string | null,
): Promise<PricingMatrix> => {
  const qs = applicationId ? `?applicationId=${encodeURIComponent(applicationId)}` : "";
  const { data } = await httpClient.get<NotifyEnvelope<PricingMatrix>>(
    `/pricing/matrix${qs}`,
    { skipGlobalLoader: true },
  );
  const m =
    data.Response ??
    { channels: [], defaults: {}, plans: [], applications: [], selectedApplicationId: null, rows: [] };
  return {
    channels: m.channels ?? [],
    defaults: lower(m.defaults),
    plans: (m.plans ?? []).map(normalizePlan),
    applications: m.applications ?? [],
    selectedApplicationId: m.selectedApplicationId ?? null,
    rows: (m.rows ?? []).map((r) => ({
      ...r,
      pricingPlanId: r.pricingPlanId ?? null,
      inheritedPlanId: r.inheritedPlanId ?? null,
      overrides: lower(r.overrides),
    })),
  };
};

export const setPricingCell = async (
  accountId: string,
  communicationChannel: string,
  pricePerUnit: number | null,
  applicationId?: string | null,
): Promise<void> => {
  await httpClient.put("/pricing/matrix/cell", {
    accountId,
    communicationChannel,
    pricePerUnit,
    applicationId: applicationId ?? null,
  });
};

/** Assign (pricingPlanId) or clear (null) the tariff of a matrix row, optionally per application. */
export const assignRowPlan = async (
  accountId: string,
  pricingPlanId: string | null,
  applicationId?: string | null,
): Promise<void> => {
  await httpClient.put("/pricing/matrix/row/plan", {
    accountId,
    pricingPlanId,
    applicationId: applicationId ?? null,
  });
};

// ---- Preset tariffs (rate plans) CRUD ----

export const getTariffs = async (): Promise<PricingPlan[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<PricingPlan[]>>(
    "/pricing/plans",
    { skipGlobalLoader: true },
  );
  return (data.Response ?? []).map(normalizePlan);
};

export const saveTariff = async (payload: {
  id?: string;
  name: string;
  prices: Record<string, number>;
}): Promise<void> => {
  await httpClient.post("/pricing/plans", {
    id: payload.id ?? "00000000-0000-0000-0000-000000000000",
    name: payload.name,
    prices: payload.prices,
  });
};

export const deleteTariff = async (id: string): Promise<void> => {
  await httpClient.delete(`/pricing/plans/${id}`);
};
